<template>
  <div :class="baseCardClasses + ' tw:rounded-md'">
    <div :class="cardMainClasses" @click="$emit('click')">
      <h2 v-if="hasSlot('header')" class="card-header tw:text-2xl tw:font-bold tw:text-center tw:mb-4">
        <slot name="header" />
      </h2>

      <div
        v-if="hasSlot('title') || hasSlot('subtitle')"
        class="card-title-wrapper tw:mb-4"
      >
        <h3 v-if="hasSlot('title')" class="card-title">
          <slot name="title" />
        </h3>

        <div v-if="hasSlot('subtitle')" class="card-subtitle">
          <slot name="subtitle" />
        </div>
      </div>

      <slot />

      <div v-if="hasSlot('footer')" class="card-footer">
        <slot name="footer" />
      </div>
    </div>

    <div v-if="hasSlot('actions')" class="card-actions">
      <slot name="actions" />
    </div>

    <div v-if="hasButtons" class="card-buttons tw:text-sm">
      <card-button
        v-for="button in buttons"
        :key="button.label"
        :button-options="button"
        @click="(key) => $emit('button-click', key)"
      />
    </div>
  </div>
</template>

<script>
  import { buildClassString } from 'Utils'
  import CardButton from 'Components/general/CardButton'

  export default {
    name: 'base-card',
    components: {
      CardButton,
    },
    computed: {
      hasButtons () {
        return this.buttons.length > 0
      },
      baseCardClasses () {
        return buildClassString('base-card', [
          {
            className: 'disabled',
            testCondition: () => this.disabled,
            bem: true,
          },
          {
            className: 'even-padding',
            testCondition: () => this.evenPadding,
            bem: true,
          },
          {
            className: 'gray-background',
            testCondition: () => this.grayBackground,
            bem: true,
          },
          {
            className: 'selected',
            testCondition: () => this.selected,
            bem: true,
          },
          {
            className: 'with-shadow',
            testCondition: () => this.withShadow,
            bem: true,
          },
          {
            className: 'with-spacing',
            testCondition: () => this.withSpacing,
            bem: true,
          },
        ])
      },
      cardMainClasses () {
        return buildClassString('card-main', [
          {
            className: 'clickable',
            testCondition: () => this.clickable,
            bem: true,
          },
          {
            className: 'overflow-visible',
            testCondition: () => this.overflowVisible,
            bem: true,
          },
        ])
      },
    },
    methods: {
      hasSlot (name = 'default') {
        return !!this.$slots[name] || !!this.$scopedSlots[name]
      },
    },
    props: {
      clickable: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      // Array of buttons for the bottom of the card
      buttons: {
        type: Array,
        default: () => [],
      },
      // Whether the card should have even padding on all sides
      evenPadding: {
        type: Boolean,
        default: false,
      },
      // Whether card should include spacing below itself
      withSpacing: {
        type: Boolean,
        default: true,
      },
      // Whether card should have a shadow around it
      withShadow: {
        type: Boolean,
        default: false,
      },
      // Whether card should have gray background or the default background
      grayBackground: {
        type: Boolean,
        default: false,
      },
      selected: {
        type: Boolean,
        default: false,
      },
      overflowVisible: {
        type: Boolean,
        default: false,
      },
    },
  }
</script>

<style lang="scss" scoped>
@use "~Styles/colors";
@use "~Styles/mixins";
@use "~Styles/variables";

  .base-card {
    background-color: variables.$card-background;
    border: variables.$card-border;
    // border-radius: variables.$card-border-radius;
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;

    transition-property: box-shadow;
    transition-duration: variables.$hover-transition-duration;
    transition-timing-function: variables.$hover-transition-timing-function;

    &--disabled {
      background-color: colors.$kommander-disabled-row-background;
    }

    &--with-spacing {
      margin-bottom: variables.$card-spacing;
    }

    &--with-shadow {
      @include mixins.kommander-shadow();
    }

    &--gray-background {
      background-color: variables.$card-background--gray;
    }

    &--selected {
      @include mixins.kommander-shadow(colors.$kommander-blue, 1);
    }

    &--even-padding {
      .card-main {
        padding-left: variables.$card-top-padding;
        padding-right: variables.$card-top-padding;
      }
    }
  }

  .card-main {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: variables.$card-padding;
    position: relative;
    overflow: hidden;

    &--clickable {
      @include mixins.transition-bg-hover(variables.$card-hover-background);
      @include mixins.clickable();
    }

    &--overflow-visible {
      overflow: unset;
    }
  }

  .card-actions {
    display: flex;
    flex-direction: column;

    position: absolute;
    top: variables.$card-top-padding;
    right: variables.$card-side-padding;
  }

  .card-title {
    @include mixins.card-title();
  }

  .card-subtitle {
    color: colors.$kommander-dark-font-color-2;
  }

  .card-footer {
    align-items: flex-end;
    color: colors.$kommander-dark-font-color-2;
    display: flex;
    justify-content: space-between;
    margin-top: 5px;
  }

  .card-buttons {
    display: flex;
    border-top: variables.$card-border;
    flex: 0;
    grid-column: 1 / span 2;
    grid-row: 2 / span 1;

    > *:not(:last-child) {
      border-right: variables.$card-border;
    }
  }
</style>
